import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ApplicationTypes } from '../../../store/application/store';
import { H2, P } from '../../common/Typography';
import { Spacer } from '../../common/Spacer';

import { styles } from '../../../styles';
import { AccountService } from '../../../services/account.service';
import { ConfigurationData } from '../../../services/configuration.service';
import { openPartnerProjectDetails } from '../../../utils/partnerLink';

const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  overflow: auto;
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  padding: 16px 24px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
  justify-content: space-between;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  color: #495057;

  &:focus {
    outline: none;
    border-color: ${styles.colors.darkBlue};
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  color: #495057;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: ${styles.colors.darkBlue};
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ClearFiltersButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: white;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 36px;
  min-height: 36px;

  &:not(:disabled) {
    color: ${styles.colors.darkBlue75};
    border-color: ${styles.colors.darkBlue75};
    background: ${styles.colors.darkBlue10};
  }

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: ${styles.colors.darkBlue};
    color: ${styles.colors.darkBlue};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    color: #6c757d;
    border-color: #e9ecef;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  margin-right: 8px;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: 12px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
  pointer-events: none;
  z-index: 1000;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 100%;
    transform: translateY(-50%);
    border: 4px solid transparent;
    border-left-color: rgba(0, 0, 0, 0.8);
  }

  ${TooltipContainer}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderCell = styled.th<{ sortable?: boolean; active?: boolean }>`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  position: relative;
  user-select: none;

  &:last-child {
    text-align: right;
  }

  ${props => props.sortable && `
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover {
      background: rgba(110, 113, 136, 0.1);
    }
  `}  

  ${props => props.active && `
    background: rgba(110, 113, 136, 0.2);
  `}
`;

const SortIcon = styled.span<{ direction: SortDirection }>`
  display: inline-block;
  margin-left: 6px;
  font-size: 12px;
  opacity: ${props => props.direction ? 1 : 0.3};
  
  &::after {
    content: '${props => props.direction === 'asc' ? '↑' : props.direction === 'desc' ? '↓' : '↕'}';
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #e9ecef;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  color: #495057;
  font-size: 14px;
  vertical-align: middle;

  &:last-child {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
`;

const ApplicationBadge = styled.span<{ applicationType: ApplicationTypes }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${styles.colors.darkBlue75};
  text-transform: capitalize;
  `;

const ProjectIdLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${styles.colors.primary};
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: #6c757d;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 6px 12px;
  border: 1px solid;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${styles.colors.darkBlue};
          border-color: ${styles.colors.darkBlue};
          color: white;
          
          &:hover {
            background: ${styles.colors.darkBlue}dd;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          border-color: #dc3545;
          color: white;
          
          &:hover {
            background: #c82333;
          }
        `;
      default:
        return `
          background: white;
          border-color: #e9ecef;
          color: #495057;
          
          &:hover {
            background: #f8f9fa;
          }
        `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6c757d;
`;

const FilterText = styled.div`
  font-size: 14px;
  margin: 0;
  color: #495057;
`;

const FilterStatusText = styled.div`
  font-size: 14px;
  margin: 0;
  color: #6c757d;
`;

const EmptySubtitle = styled.div`
  font-size: 14px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #495057;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #495057;
`;

const PaginationButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: white;
  color: #495057;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: ${styles.colors.darkBlue75};
    color: ${styles.colors.darkBlue75};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f8f9fa;
    color: #6c757d;
  }
`;

const PageNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid ${styles.colors.darkBlue};
  border-radius: 4px;
  background: ${styles.colors.darkBlue};
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: ${styles.colors.darkBlue}dd;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

function getApplicationDisplayName(t: any, applicationType: ApplicationTypes): string {
  const displayNames: Record<ApplicationTypes, string> = {
    [ApplicationTypes.FLAT_ROOF]: t('account.dashboard.flatRoof'),
    [ApplicationTypes.SLOPED_ROOF]: t('account.dashboard.slopedRoof'),
    [ApplicationTypes.CAVITY_WALL]: t('account.dashboard.cavityWall'),
    [ApplicationTypes.OUTER_WALL]: t('account.dashboard.outerWall'),
    [ApplicationTypes.FLOOR]: t('account.dashboard.floor'),
    [ApplicationTypes.SOL]: t('account.dashboard.sol'),
    [ApplicationTypes.TOIT_EN_PENTE]: t('account.dashboard.toitEnPente'),
    [ApplicationTypes.INNER_WALL]: t('account.dashboard.innerWall'),
    [ApplicationTypes.ATTIC_FLOOR]: t('account.dashboard.atticFloor'),
    [ApplicationTypes.BASEMENT]: t('account.dashboard.basement'),
    [ApplicationTypes.ATTIC_SLOPED_ROOF]: t('account.dashboard.atticSlopedRoof'),
  };
  return displayNames[applicationType] || applicationType;
}

interface SavedConfigurationsTableProps {
  configurations: ConfigurationData[];
  isAdminView?: boolean;
  onEdit?: (configuration: ConfigurationData) => void;
  onDelete?: (configuration: ConfigurationData) => void;
  onLoad?: (configuration: ConfigurationData) => void;
}

type SortField = 'application_type' | 'my_iko_id' | 'display_name' | 'language' | 'created_at' | 'updated_at';
type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  field: SortField | null;
  direction: SortDirection;
}

interface PaginationState {
  currentPage: number;
  itemsPerPage: number;
}

export const SavedConfigurationsTable: React.FC<SavedConfigurationsTableProps> = ({
  isAdminView = false,
  configurations,
  onEdit,
  onDelete,
  onLoad,
}) => {
  const { t } = useTranslation() as any;
  const [selectedApplicationType, setSelectedApplicationType] = useState<ApplicationTypes | 'ALL'>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortState, setSortState] = useState<SortState>({
    field: 'created_at',
    direction: 'desc'
  });
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    itemsPerPage: 25
  });

  // Get unique application types from configurations
  const availableApplicationTypes = useMemo(() => {
    const types = Array.from(new Set(configurations.map(configuration => configuration.application_type)));
    return types.sort();
  }, [configurations]);

  // Filter and sort configurations
  const filteredAndSortedConfigurations = useMemo(() => {
    let filtered = configurations;

    // Filter by application type
    if (selectedApplicationType !== 'ALL') {
      filtered = filtered.filter(config => config.application_type === selectedApplicationType);
    }

    // Filter by search term in display_name
    if (searchTerm.trim()) {
      filtered = filtered.filter(config =>
        config.display_name?.toLowerCase().includes(searchTerm.toLowerCase().trim())
      );
    }

    // Sort configurations
    if (sortState.field && sortState.direction) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortState.field) {
          case 'application_type':
            aValue = getApplicationDisplayName(t, a.application_type);
            bValue = getApplicationDisplayName(t, b.application_type);
            break;
          case 'my_iko_id':
            aValue = a.my_iko_id || '';
            bValue = b.my_iko_id || '';
            break;
          case 'display_name':
            aValue = a.display_name || '';
            bValue = b.display_name || '';
            break;
          case 'language':
            aValue = a.language || '';
            bValue = b.language || '';
            break;
          case 'created_at':
            aValue = new Date(a.created_at || 0).getTime();
            bValue = new Date(b.created_at || 0).getTime();
            break;
          case 'updated_at':
            aValue = new Date(a.updated_at || 0).getTime();
            bValue = new Date(b.updated_at || 0).getTime();
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortState.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortState.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [configurations, selectedApplicationType, searchTerm, sortState, t]);

  // Calculate pagination data
  const paginationData = useMemo(() => {
    const totalItems = filteredAndSortedConfigurations.length;
    const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    const currentItems = filteredAndSortedConfigurations.slice(startIndex, endIndex);

    return {
      totalItems,
      totalPages,
      startIndex,
      endIndex: Math.min(endIndex, totalItems),
      currentItems,
      hasNextPage: pagination.currentPage < totalPages,
      hasPreviousPage: pagination.currentPage > 1
    };
  }, [filteredAndSortedConfigurations, pagination]);

  const handleOpen = (configuration: ConfigurationData) => {
    if (!onLoad) return;
    onLoad(configuration);
  };

  const handleEdit = (configuration: ConfigurationData) => {
    if (!onEdit) return;
    onEdit(configuration);
  };

  const handleDelete = (configuration: ConfigurationData) => {
    if (!onDelete) return;
    if (window.confirm(t('account.configuration.confirmDelete'))) {
      onDelete(configuration);
    }
  };

  const handleClearFilters = () => {
    setSelectedApplicationType('ALL');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field: SortField) => {
    setSortState(prevState => {
      if (prevState.field === field) {
        // Cycle through: asc -> desc -> null -> asc
        const newDirection =
          prevState.direction === 'asc' ? 'desc' :
            prevState.direction === 'desc' ? null : 'asc';
        return {
          field: newDirection ? field : 'created_at',
          direction: newDirection || 'desc'
        };
      } else {
        return { field, direction: 'asc' };
      }
    });
  };

  const handleResetSort = () => {
    setSortState({
      field: 'created_at',
      direction: 'desc'
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination({
      currentPage: 1,
      itemsPerPage: newPageSize
    });
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [selectedApplicationType, searchTerm, sortState]);

  const handleSendOutReports = () => {
    if (!isAdminView) return

    let result = window.confirm('Are you sure you want to send out reports to all marketing departments?')
    if (!result) return
    result = window.confirm('Are you 100% sure?')
    if (!result) return

    AccountService.sendOutReports()
      .then(() => {
        alert('Attempted to send reports successfully');
      }).catch(() => {
        alert('Failed to send reports');
        window.location.reload();
      })
  }

  const handleExportCSV = () => {
    if (!isAdminView) return

    // Prepare CSV headers
    const headers = [
      'ID',
      'Application Type',
      'Display Name',
      'My IKO ID',
      'Language',
      'Created At',
      'Updated At'
    ];

    // Prepare CSV rows
    const rows = filteredAndSortedConfigurations.map(config => [
      config.id || '',
      getApplicationDisplayName(t, config.application_type),
      config.display_name || '',
      config.my_iko_id || '',
      config.language || '',
      dayjs(config.created_at).format('DD/MM/YYYY HH:mm:ss'),
      dayjs(config.updated_at).format('DD/MM/YYYY HH:mm:ss')
    ]);

    // Create CSV content
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `configurations-export-${dayjs().format('YYYY-MM-DD-HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const hasActiveFilters = selectedApplicationType !== 'ALL' || searchTerm.trim() !== '';
  const hasCustomSort = sortState.field !== 'created_at' || sortState.direction !== 'desc';

  if (configurations.length === 0) {
    return (
      <TableContainer>
        <TableHeader>
          <H2>{t('account.configuration.title')}</H2>
        </TableHeader>
        <EmptyState>
          <P>{t('account.configuration.noConfigurationsFound')}</P>
          {!isAdminView && (
            <>
              <Spacer heightPx={8} />
              <EmptySubtitle>{t('account.configuration.createFirstConfiguration')}</EmptySubtitle>
            </>
          )}
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <H2>{t('account.configuration.title')}</H2>
        {isAdminView && configurations.length > 0 && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {process.env.NODE_ENV !== "production" && (
              <ExportButton onClick={handleSendOutReports}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM9 13V19H7V13H9ZM15 15V19H17V15H15ZM11 11V19H13V11H11Z" fill="currentColor" />
                </svg>
                {t('account.configuration.sendOutReports') || 'Send reports'}
              </ExportButton>
            )}
            <ExportButton onClick={handleExportCSV}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
              {t('account.configuration.exportCSV') || 'Export CSV'}
            </ExportButton>
          </div>
        )}
      </div>
      <FilterContainer>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <FilterText>{t('account.configuration.filterApplicationTitle')}:</FilterText>
          <FilterSelect
            value={selectedApplicationType}
            onChange={(e) => setSelectedApplicationType(e.target.value as ApplicationTypes | 'ALL')}
          >
            <option value="ALL">{t('account.configuration.filterAllApplication')}</option>
            {availableApplicationTypes.map(type => (
              <option key={type} value={type}>
                {getApplicationDisplayName(t, type)}
              </option>
            ))}
          </FilterSelect>
          <SearchInput
            type="text"
            placeholder={t('account.configuration.searchPlaceholder') || 'Search configurations...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <FilterStatusText>
            {t('account.configuration.filterTitle').replace('[count]', paginationData.totalItems).replace('[total]', configurations.length)}
          </FilterStatusText>
          <TooltipContainer>
            <ClearFiltersButton
              onClick={handleResetSort}
              disabled={!hasCustomSort}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 18h6v-2H3v2zM3 6v2h18V6H3zm0 7h12v-2H3v2z" />
              </svg>
            </ClearFiltersButton>
            <Tooltip>{t('account.configuration.resetSort') || 'Reset sort'}</Tooltip>
          </TooltipContainer>
          <TooltipContainer>
            <ClearFiltersButton
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </ClearFiltersButton>
            <Tooltip>{t('account.configuration.clearFilters') || 'Clear filters'}</Tooltip>
          </TooltipContainer>
        </div>
      </FilterContainer>

      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              <TableHeaderCell
                sortable
                active={sortState.field === 'application_type'}
                onClick={() => handleSort('application_type')}
              >
                {t('account.configuration.applicationTypeTitle')}
                <SortIcon direction={sortState.field === 'application_type' ? sortState.direction : null} />
              </TableHeaderCell>
              {isAdminView && (
                <TableHeaderCell
                  sortable
                  active={sortState.field === 'my_iko_id'}
                  onClick={() => handleSort('my_iko_id')}
                >
                  {t('account.configuration.myIkoIdTitle')}
                  <SortIcon direction={sortState.field === 'my_iko_id' ? sortState.direction : null} />
                </TableHeaderCell>
              )}
              <TableHeaderCell
                sortable
                active={sortState.field === 'display_name'}
                onClick={() => handleSort('display_name')}
              >
                {t('account.configuration.displayNameTitle')}
                <SortIcon direction={sortState.field === 'display_name' ? sortState.direction : null} />
              </TableHeaderCell>
              <TableHeaderCell>
                {t('account.configuration.projectIdTitle') || 'Project ID'}
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                active={sortState.field === 'language'}
                onClick={() => handleSort('language')}
              >
                {t('account.configuration.languageTitle')}
                <SortIcon direction={sortState.field === 'language' ? sortState.direction : null} />
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                active={sortState.field === 'created_at'}
                onClick={() => handleSort('created_at')}
              >
                {t('account.configuration.createdAtTitle')}
                <SortIcon direction={sortState.field === 'created_at' ? sortState.direction : null} />
              </TableHeaderCell>
              {isAdminView && (
                <TableHeaderCell
                  sortable
                  active={sortState.field === 'updated_at'}
                  onClick={() => handleSort('updated_at')}
                >
                  {t('account.configuration.updatedAtTitle')}
                  <SortIcon direction={sortState.field === 'updated_at' ? sortState.direction : null} />
                </TableHeaderCell>
              )}
              <TableHeaderCell>{t('account.configuration.actionsTitle')}</TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {paginationData.currentItems.map((configuration: ConfigurationData, index: number) => (
              <TableRow key={configuration.id || index}>
                {/* <TableCell>{configuration.id}</TableCell> */}
                <TableCell>
                  <ApplicationBadge applicationType={configuration.application_type}>
                    {getApplicationDisplayName(t, configuration.application_type)}
                  </ApplicationBadge>
                </TableCell>
                {isAdminView && (
                  <TableCell>{configuration.my_iko_id}</TableCell>
                )}
                <TableCell>{configuration.display_name || "N/A"}</TableCell>
                <TableCell>
                  {configuration.project_id ? (
                    <ProjectIdLink
                      href={`${process.env.REACT_APP_MY_IKO_URL}project-details?project_id=${configuration.project_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        openPartnerProjectDetails({
                          recordId: configuration.record_id,
                          projectId: configuration.project_id,
                        });
                      }}
                    >
                      {configuration.project_id}
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </ProjectIdLink>
                  ) : (
                    "N/A"
                  )}
                </TableCell>
                <TableCell>{configuration.language}</TableCell>
                <TableCell>{dayjs(configuration.created_at).format('DD/MM/YYYY - HH:mm')}</TableCell>
                {isAdminView && (
                  <TableCell>{dayjs(configuration.updated_at).format('DD/MM/YYYY - HH:mm')}</TableCell>
                )}
                <TableCell>
                  <ActionButtons>
                    <ActionButton
                      variant="primary"
                      onClick={() => handleOpen(configuration)}
                    >
                      {t('account.configuration.actionOpen')}
                    </ActionButton>
                    {!isAdminView && (
                      <>
                        <ActionButton
                          variant="secondary"
                          onClick={() => handleEdit(configuration)}
                        >
                          {t('account.configuration.actionEdit')}
                        </ActionButton>
                        <ActionButton
                          variant="danger"
                          onClick={() => handleDelete(configuration)}
                        >
                          {t('account.configuration.actionDelete')}
                        </ActionButton>
                      </>
                    )}
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {paginationData.totalItems > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              {t('account.configuration.paginationInfo', {
                start: paginationData.startIndex + 1,
                end: paginationData.endIndex,
                total: paginationData.totalItems
              }) || `Showing ${paginationData.startIndex + 1}-${paginationData.endIndex} of ${paginationData.totalItems} items`}
            </PaginationInfo>

            <PaginationControls>
              <PageSizeSelector>
                <span>{t('account.configuration.itemsPerPage') || 'Items per page'}:</span>
                <FilterSelect
                  value={pagination.itemsPerPage}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </FilterSelect>
              </PageSizeSelector>

              <PageNavigation>
                <PaginationButton
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!paginationData.hasPreviousPage}
                >
                  {t('account.configuration.pagePrev') || 'Previous'}
                </PaginationButton>

                <span style={{ fontSize: '14px', color: '#495057' }}>
                  {t('account.configuration.pageInfo', {
                    current: pagination.currentPage,
                    total: paginationData.totalPages
                  }) || `Page ${pagination.currentPage} of ${paginationData.totalPages}`}
                </span>

                <PaginationButton
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!paginationData.hasNextPage}
                >
                  {t('account.configuration.pageNext') || 'Next'}
                </PaginationButton>
              </PageNavigation>
            </PaginationControls>
          </PaginationContainer>
        )}
      </TableContainer>
    </div>
  );
};
